import { useState, useEffect } from "react";
import { auth, firestore } from "./firebase";
import {
  setDoc,
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

const garageCoords = {
  latitude: 51.483361,
  longitude: 0.06625,
};
const allowedRadiusMeters = 30;

function getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371e3; // meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}


function formatTime(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function Home() {
  const [name, setName] = useState("");
  const [messages, setMessages] = useState([]);
  const [todayActions, setTodayActions] = useState({});
  const [refreshActions, setRefreshActions] = useState(false);
  const [pastRecords, setPastRecords] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);
  const [activeTab, setActiveTab] = useState("attendance");
  const [isSunday, setIsSunday] = useState(false);

  useEffect(() => {
    const today = new Date();
    setIsSunday(today.getDay() === 0);

    const user = auth.currentUser;
    if (user && user.email) {
      const docId = user.email.replace(/\./g, ",");
      const userDocRef = doc(firestore, "users", docId);
      getDoc(userDocRef).then((docSnap) => {
        if (docSnap.exists()) {
          setName(docSnap.data().name || "");
        }
      });
    }
  }, []);

  useEffect(() => {
    const fetchTodayActions = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const today = new Date().toISOString().split("T")[0];
      const docRef = doc(firestore, "attendanceRecords", `${user.uid}_${today}`);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setTodayActions(docSnap.data());
      else setTodayActions({});
    };
    if (activeTab === "attendance") {
      fetchTodayActions();
    }
  }, [refreshActions, activeTab]);

  const fetchPastAttendance = async () => {
    setLoadingPast(true);
    const user = auth.currentUser;
    if (!user) {
      setMessages((prev) => [...prev, "❌ No authenticated user found."]);
      setLoadingPast(false);
      return;
    }

    const today = new Date();
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 30);
    const pastDateStr = pastDate.toISOString().split("T")[0];

    try {
      const q = query(
        collection(firestore, "attendanceRecords"),
        where("userId", "==", user.uid),
        where("date", ">=", pastDateStr),
        orderBy("date", "desc"),
        limit(30)
      );
      const querySnapshot = await getDocs(q);
      const records = [];
      querySnapshot.forEach((doc) => {
        records.push(doc.data());
      });
      setPastRecords(records);
    } catch (e) {
      setMessages((prev) => [...prev, `❌ Error fetching past attendance: ${e.message}`]);
    }
    setLoadingPast(false);
  };

  useEffect(() => {
    if (activeTab === "past") {
      fetchPastAttendance();
    }
  }, [activeTab]);

  const saveAttendance = async (field, timeValue) => {
    const user = auth.currentUser;
    if (!user) return;
    const today = new Date().toISOString().split("T")[0];
    const attendanceId = `${user.uid}_${today}`;

    let userName = "";
    try {
      const userDocRef = doc(firestore, "users", user.email.replace(/\./g, ","));
      const userSnap = await getDoc(userDocRef);
      if (userSnap.exists()) userName = userSnap.data().name || "";
    } catch {}

    await setDoc(
      doc(firestore, "attendanceRecords", attendanceId),
      {
        userId: user.uid,
        name: userName,
        date: today,
        [field]: timeValue,
      },
      { merge: true }
    );
  };

  const handleAction = async (action) => {
    if (isSunday) {
      setMessages((prev) => [...prev, "❌ Actions are disabled on Sundays."]);
      return;
    }
    
    const user = auth.currentUser;
    if (!user) {
      setMessages((prev) => [...prev, "❌ No authenticated user found. Please log in again."]);
      return;
    }
    
    const today = new Date().toISOString().split("T")[0];
    const docRef = doc(firestore, "attendanceRecords", `${user.uid}_${today}`);
    const freshDocSnap = await getDoc(docRef);
    const freshData = freshDocSnap.exists() ? freshDocSnap.data() : {};

    if (freshData[action]) {
      setMessages((prev) => [...prev, `❌ You already did '${action}' today at ${new Date(freshData[action]).toLocaleTimeString()}.`]);
      return;
    }

    if (!navigator.geolocation) {
      setMessages((prev) => [...prev, "❌ Geolocation not supported by your browser."]);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const distance = getDistanceFromLatLonInMeters(
          pos.coords.latitude,
          pos.coords.longitude,
          garageCoords.latitude,
          garageCoords.longitude
        );
        if (distance > allowedRadiusMeters) {
          setMessages((prev) => [...prev, "❌ You are not at the garage location. Please move closer."]);
          return;
        }

        if (!freshData.checkIn && action !== "checkIn") {
          setMessages((prev) => [...prev, "❌ You must check in before other actions."]);
          return;
        }
        if (freshData.checkOut) {
          setMessages((prev) => [...prev, "❌ You have already checked out for today."]);
          return;
        }
        if (freshData.lunchStart && !freshData.lunchEnd && action !== "lunchEnd") {
          setMessages((prev) => [...prev, "❌ Please end your lunch break before other actions."]);
          return;
        }

        const now = new Date();
// In the handleAction function, replace the check-in and check-out validation blocks with:

if (action === "checkIn") {
  // Check-in allowed between 8:45 AM and 6:20 PM
  if (
    now.getHours() < 8 || 
    (now.getHours() === 8 && now.getMinutes() < 45) ||
    now.getHours() > 18 ||
    (now.getHours() === 18 && now.getMinutes() > 20)
  ) {
    setMessages((prev) => [...prev, "❌ Check-in only allowed between 8:45 AM and 6:20 PM."]);
    return;
  }
}

if (action === "checkOut") {
  // Check-out allowed before 6:20 PM
  if (now.getHours() > 18 || (now.getHours() === 18 && now.getMinutes() >= 20)) {
    setMessages((prev) => [...prev, "❌ Check-out only allowed before 6:20 PM."]);
    return;
  }
}
      

        try {
          await saveAttendance(action, now.toISOString());
          // setMessages((prev) => [...prev, `✅ ${action} successful at ${now.toLocaleTimeString()}.`]);
          setRefreshActions((prev) => !prev);

          if (action === "checkOut") {
            const checkInDate = freshData.checkIn ? new Date(freshData.checkIn) : null;
            if (checkInDate) {
              const diffMs = now - checkInDate;
              const hours = Math.floor(diffMs / 3600000);
              const minutes = Math.floor((diffMs % 3600000) / 60000);
              setMessages((prev) => [...prev, `⏱️ Total worked: ${hours}h ${minutes}m.`]);
            }
          }
        } catch (e) {
          setMessages((prev) => [...prev, `❌ Error: ${e.message}`]);
        }
      },
      (err) => {
        setMessages((prev) => [...prev, `❌ Location error: ${err.message}`]);
      }
    );
  };

  const handleLogout = () => auth.signOut();

  // Button states
  const hasCheckedIn = !!todayActions.checkIn;
  const hasLunchStart = !!todayActions.lunchStart;
  const hasLunchEnd = !!todayActions.lunchEnd;
  const hasCheckedOut = !!todayActions.checkOut;

  return (
    <div style={{ 
      maxWidth: 480, 
      margin: "auto", 
      padding: 20, 
      fontFamily: "'Segoe UI', Roboto, sans-serif",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh"
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: "1px solid #e0e0e0"
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: "1.5rem",
          fontWeight: 600,
          color: "#2c3e50"
        }}>Welcome, {name || "User"}</h2>
        <button 
          onClick={handleLogout} 
          style={{ 
            backgroundColor: "transparent",
            color: "#dc3545",
            padding: "8px 12px",
            border: "1px solid #dc3545",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "0.9rem"
          }}
        >
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: "flex", 
        marginBottom: 24,
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
      }}>
        <button
          style={{
            flex: 1,
            padding: 12,
            backgroundColor: activeTab === "attendance" ? "#0d6efd" : "#f8f9fa",
            color: activeTab === "attendance" ? "white" : "#495057",
            border: "none",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s"
          }}
          onClick={() => setActiveTab("attendance")}
        >
          Attendance
        </button>
        <button
          style={{
            flex: 1,
            padding: 12,
            backgroundColor: activeTab === "past" ? "#0d6efd" : "#f8f9fa",
            color: activeTab === "past" ? "white" : "#495057",
            border: "none",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s"
          }}
          onClick={() => setActiveTab("past")}
        >
          Past Records
        </button>
      </div>

      {activeTab === "attendance" && (
        <>
          {isSunday && (
            <div style={{ 
              marginBottom: 20, 
              padding: 12,
              backgroundColor: "#fff3cd",
              color: "#856404",
              borderRadius: 6,
              borderLeft: "4px solid #ffeeba",
              fontWeight: 500
            }}>
              All actions are disabled on Sunday.
            </div>
          )}
          
          {/* Attendance Status Cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 24
          }}>
            <div style={{
              backgroundColor: "white",
              padding: 16,
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderLeft: `4px solid ${hasCheckedIn ? "#28a745" : "#e9ecef"}`
            }}>
              <div style={{ 
                fontSize: "0.85rem",
                color: "#6c757d",
                marginBottom: 4
              }}>Check-In</div>
              <div style={{ 
                fontSize: "1.1rem",
                fontWeight: 500,
                color: hasCheckedIn ? "#28a745" : "#6c757d"
              }}>
                {formatTime(todayActions.checkIn) || "-"}
              </div>
            </div>
            
            <div style={{
              backgroundColor: "white",
              padding: 16,
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderLeft: `4px solid ${hasCheckedOut ? "#28a745" : "#e9ecef"}`
            }}>
              <div style={{ 
                fontSize: "0.85rem",
                color: "#6c757d",
                marginBottom: 4
              }}>Check-Out</div>
              <div style={{ 
                fontSize: "1.1rem",
                fontWeight: 500,
                color: hasCheckedOut ? "#28a745" : "#6c757d"
              }}>
                {formatTime(todayActions.checkOut) || "-"}
              </div>
            </div>
            
            <div style={{
              backgroundColor: "white",
              padding: 16,
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderLeft: `4px solid ${hasLunchStart ? "#fd7e14" : "#e9ecef"}`
            }}>
              <div style={{ 
                fontSize: "0.85rem",
                color: "#6c757d",
                marginBottom: 4
              }}>Lunch Start</div>
              <div style={{ 
                fontSize: "1.1rem",
                fontWeight: 500,
                color: hasLunchStart ? "#fd7e14" : "#6c757d"
              }}>
                {formatTime(todayActions.lunchStart) || "-"}
              </div>
            </div>
            
            <div style={{
              backgroundColor: "white",
              padding: 16,
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderLeft: `4px solid ${hasLunchEnd ? "#fd7e14" : "#e9ecef"}`
            }}>
              <div style={{ 
                fontSize: "0.85rem",
                color: "#6c757d",
                marginBottom: 4
              }}>Lunch End</div>
              <div style={{ 
                fontSize: "1.1rem",
                fontWeight: 500,
                color: hasLunchEnd ? "#fd7e14" : "#6c757d"
              }}>
                {formatTime(todayActions.lunchEnd) || "-"}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: 12,
            marginBottom: 24
          }}>
            <button 
              disabled={hasCheckedIn || isSunday}
              onClick={() => handleAction("checkIn")}
              style={{
                padding: 14,
                backgroundColor: hasCheckedIn ? "#e9ecef" : "#28a745",
                color: hasCheckedIn ? "#6c757d" : "white",
                border: "none",
                borderRadius: 8,
                cursor: hasCheckedIn ? "not-allowed" : "pointer",
                fontWeight: 500,
                fontSize: "1rem",
                transition: "all 0.2s",
                boxShadow: hasCheckedIn ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
                opacity: hasCheckedIn ? 0.7 : 1
              }}
            >
              {hasCheckedIn ? "Checked In" : "Check-In"}
            </button>
            
            <button 
              disabled={hasCheckedOut || isSunday || !hasCheckedIn || (hasLunchStart && !hasLunchEnd)}
              onClick={() => handleAction("checkOut")}
              style={{
                padding: 14,
                backgroundColor: hasCheckedOut ? "#e9ecef" : "#28a745",
                color: hasCheckedOut ? "#6c757d" : "white",
                border: "none",
                borderRadius: 8,
                cursor: hasCheckedOut ? "not-allowed" : "pointer",
                fontWeight: 500,
                fontSize: "1rem",
                transition: "all 0.2s",
                boxShadow: hasCheckedOut ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
                opacity: hasCheckedOut ? 0.7 : 1
              }}
            >
              {hasCheckedOut ? "Checked Out" : "Check-Out"}
            </button>
            
            <button 
              disabled={hasLunchStart || isSunday || !hasCheckedIn || hasCheckedOut}
              onClick={() => handleAction("lunchStart")}
              style={{
                padding: 14,
                backgroundColor: hasLunchStart ? "#e9ecef" : "#fd7e14",
                color: hasLunchStart ? "#6c757d" : "white",
                border: "none",
                borderRadius: 8,
                cursor: hasLunchStart ? "not-allowed" : "pointer",
                fontWeight: 500,
                fontSize: "1rem",
                transition: "all 0.2s",
                boxShadow: hasLunchStart ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
                opacity: hasLunchStart ? 0.7 : 1
              }}
            >
              {hasLunchStart ? "Lunch Started" : "Start Lunch"}
            </button>
            
            <button 
              disabled={hasLunchEnd || isSunday || !hasLunchStart || hasCheckedOut}
              onClick={() => handleAction("lunchEnd")}
              style={{
                padding: 14,
                backgroundColor: hasLunchEnd ? "#e9ecef" : "#fd7e14",
                color: hasLunchEnd ? "#6c757d" : "white",
                border: "none",
                borderRadius: 8,
                cursor: hasLunchEnd ? "not-allowed" : "pointer",
                fontWeight: 500,
                fontSize: "1rem",
                transition: "all 0.2s",
                boxShadow: hasLunchEnd ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
                opacity: hasLunchEnd ? 0.7 : 1
              }}
            >
              {hasLunchEnd ? "Lunch Ended" : "End Lunch"}
            </button>
          </div>
        </>
      )}

      {activeTab === "past" && (
        <>
          <h3 style={{ 
            fontSize: "1.25rem",
            marginBottom: 16,
            color: "#2c3e50"
          }}>Past 30 Days Attendance</h3>
          {loadingPast ? (
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 100
            }}>
              <div style={{
                width: 40,
                height: 40,
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #0d6efd",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}></div>
            </div>
          ) : pastRecords.length === 0 ? (
            <div style={{
              backgroundColor: "white",
              padding: 20,
              borderRadius: 8,
              textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              color: "#6c757d"
            }}>
              No past records found.
            </div>
          ) : (
            <div style={{
              backgroundColor: "white",
              borderRadius: 8,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    textAlign: "left"
                  }}>
                    <th style={{ padding: "12px 16px", borderBottom: "1px solid #e9ecef" }}>Date</th>
                    <th style={{ padding: "12px 16px", borderBottom: "1px solid #e9ecef" }}>Check-In</th>
                    <th style={{ padding: "12px 16px", borderBottom: "1px solid #e9ecef" }}>Check-Out</th>
                  </tr>
                </thead>
                <tbody>
                  {pastRecords.map((rec, i) => (
                    <tr key={i} style={{
                      borderBottom: i < pastRecords.length - 1 ? "1px solid #e9ecef" : "none",
                      backgroundColor: i % 2 === 0 ? "white" : "#f8f9fa"
                    }}>
                      <td style={{ padding: "12px 16px", color: "#495057" }}>{rec.date}</td>
                      <td style={{ padding: "12px 16px", color: rec.checkIn ? "#28a745" : "#6c757d" }}>
                        {formatTime(rec.checkIn) || "-"}
                      </td>
                      <td style={{ padding: "12px 16px", color: rec.checkOut ? "#28a745" : "#6c757d" }}>
                        {formatTime(rec.checkOut) || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div style={{ 
          marginTop: 24,
          backgroundColor: "white",
          borderRadius: 8,
          padding: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          {messages.slice(-3).map((msg, i) => (
            <div key={i} style={{ 
              marginBottom: i < messages.length - 1 ? 8 : 0,
              color: msg.startsWith("❌") ? "#dc3545" : msg.startsWith("✅") ? "#28a745" : "#17a2b8",
              fontSize: "0.9rem"
            }}>
              {msg}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        button:disabled {
          opacity: 0.7;
          cursor: not-allowed !important;
        }
      `}</style>
    </div>
  );
}